import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FadeInUp } from 'app/components/Common/FadeInUp'
import { Line } from 'app/components/Common/Line'
import { FlexBox } from 'app/components/Layout/FlexBox'
import {
  Props as ServiceProps,
  Service,
} from 'app/components/ServicesList/Service'
import { theme } from 'app/theme'
import React, { memo } from 'react'

export interface Props {
  legend?: string
  services: ServiceProps[]
  title?: string
  variant?: Variant
}

export const IconsList = memo(function IconsList({
  legend,
  services,
  title,
  variant = 'default',
}: Props) {
  if (services.length < 1) {
    return null
  }

  return (
    <Container
      dial={4}
      row
      space="between"
      stretch
      tag="section"
      variant={variant}
      wrap
    >
      <LeftSide dial={4}>
        {title ? (
          <FadeInUp>
            <Title>
              {title}
              <Line />
            </Title>
          </FadeInUp>
        ) : null}
      </LeftSide>
      <RightSide dial={4}>
        <Services row wrap>
          {services.map((item, index) => (
            <Service key={index} {...item} />
          ))}
        </Services>

        {legend ? (
          <FadeInUp>
            <Legend dangerouslySetInnerHTML={{ __html: legend }} />
          </FadeInUp>
        ) : null}
      </RightSide>
    </Container>
  )
})

const Container = styled(FlexBox)<ContainerProps>`
  margin: 9.375rem 0;

  ${({ variant }) => {
    switch (variant) {
      case 'dark':
        return css`
          > div {
            &:first-of-type {
              background: ${theme.colors.variants.neutralDark2};
              h2 {
                color: ${theme.colors.variants.neutralLight4};
                > div {
                  background: ${theme.colors.variants.neutralLight4};
                }
              }
            }
          }
        `
      case 'invert':
        return css`
          > div {
            &:first-of-type {
              background: ${theme.colors.variants.secondaryLight};
              order: 2;
              h2 {
                color: ${theme.colors.variants.neutralLight4};
                > div {
                  background: ${theme.colors.variants.neutralLight4};
                }
              }
            }
            &:last-of-type {
              padding-right: 0;
              padding-left: 10.556vw;

              @media (max-width: 1199px) {
                padding-left: 1.875rem;
              }

              @media (max-width: 1023px) {
                order: 2;
              }

              @media (max-width: 767px) {
                padding-right: 1.25rem;
                padding-left: 1.25rem;
              }
            }
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    margin: 7.5rem 0;
  }

  @media (max-width: 767px) {
    margin: 5.625rem 0;
  }
`

const LeftSide = styled(FlexBox)`
  width: 35.5%;
  min-height: 45.6vh;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding: 0 5.625vw 0 10.556vw;

  @media (max-width: 1199px) {
    width: calc(50% - 3.75rem);
    padding: 0 1.875rem;
  }

  @media (max-width: 1023px) {
    width: 100%;
    min-height: 0;
    justify-content: center;
    padding: 3.75rem 1.875rem;
    text-align: center;

    > div {
      margin: auto;
    }
  }

  @media (max-width: 767px) {
    padding-right: 1.25rem;
    padding-left: 1.25rem;
  }
`

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 600;
  line-height: 2.875rem;

  @media (max-width: 1023px) {
    > div {
      margin-right: auto;
      margin-left: auto;
    }
  }
`

const RightSide = styled(FlexBox)`
  width: 50%;
  padding-right: 10.556vw;

  @media (max-width: 1199px) {
    padding-right: 1.875rem;
  }

  @media (max-width: 1023px) {
    width: 100%;
    justify-content: center;
    padding-right: 0;
    padding-top: 3rem;

    > div {
      width: 100%;
      text-align: center;
    }
  }

  @media (max-width: 767px) {
    padding-right: 1.25rem;
    padding-left: 1.25rem;
  }
`

const Services = styled(FlexBox)`
  width: 100%;
  > div {
    margin-top: 2.8125rem;
    &:nth-of-type(1),
    &:nth-of-type(2) {
      margin-top: 0;
    }
  }

  @media (max-width: 767px) {
    > div {
      &:nth-of-type(1) {
        margin-top: 0;
      }
      &:nth-of-type(2) {
        margin-top: 2.8125rem;
      }
    }
  }
`

const Legend = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.1875rem;
  margin-top: 6.25rem;

  @media (max-width: 1023px) {
    margin-top: 3.75rem;
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'dark' | 'invert'
